import { useCallback, useState } from 'react'

import type { CommandBarAction, FrecencyStorage } from '../actions/types'
import { getSectionPriority } from '../constants'
import {
  loadFrecencyStorage,
  recordActionUse,
  calculateFrecencyScore,
} from '../utils/frecency'

/**
 * Return value from the useFrecency hook.
 */
interface UseFrecencyResult {
  /** Record that an action was used */
  recordUse: (actionId: string) => void
  /** Sort actions by frecency (highest first) */
  sortByFrecency: <T extends CommandBarAction>(actions: T[]) => T[]
  /** Get the frecency score for an action */
  getScore: (actionId: string) => number
  /** Raw frecency storage data */
  frecencyData: FrecencyStorage
}

/**
 * Hook for managing frecency-based action sorting.
 *
 * Frecency combines frequency and recency to determine which actions
 * should appear higher in the command bar results.
 */
export function useFrecency(): UseFrecencyResult {
  // Track storage in state to trigger re-renders when it changes
  const [storage, setStorage] = useState<FrecencyStorage>(() =>
    loadFrecencyStorage(),
  )

  const recordUse = useCallback((actionId: string) => {
    const updatedStorage = recordActionUse(actionId)
    setStorage(updatedStorage)
  }, [])

  const sortByFrecency = useCallback(
    <T extends CommandBarAction>(actions: T[]): T[] => {
      return actions.toSorted((a, b) => {
        // First, sort by section priority
        const sectionPriorityA = getSectionPriority(a.section)
        const sectionPriorityB = getSectionPriority(b.section)
        if (sectionPriorityA !== sectionPriorityB) {
          return sectionPriorityB - sectionPriorityA
        }

        // Within section, sort by combined priority and frecency
        const frecencyA = storage[a.id]
          ? calculateFrecencyScore(storage[a.id])
          : 0
        const frecencyB = storage[b.id]
          ? calculateFrecencyScore(storage[b.id])
          : 0

        // Combine base priority with frecency
        const totalA = (a.priority ?? 0) + frecencyA * 10
        const totalB = (b.priority ?? 0) + frecencyB * 10

        return totalB - totalA
      })
    },
    [storage],
  )

  const getScore = useCallback(
    (actionId: string): number => {
      const data = storage[actionId]
      if (!data) return 0
      return calculateFrecencyScore(data)
    },
    [storage],
  )

  return {
    recordUse,
    sortByFrecency,
    getScore,
    frecencyData: storage,
  }
}
